import React, { useState } from 'react';
import { X } from 'lucide-react';
import { supabase } from '../supabaseClient';

const EMAIL_REGEX = /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/;

const INQUIRY_TYPES = [
  'サービスに関する質問',
  '不具合の報告',
  'コンテンツの修正依頼',
  'その他',
];

export function ContactModal({ onClose }) {
  const [form, setForm] = useState({
    name: '',
    email: '',
    inquiryType: '',
    subject: '',
    content: '',
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const validate = () => {
    const e = {};
    if (form.name.trim().length < 2) e.name = 'お名前は2文字以上で入力してください';
    if (!EMAIL_REGEX.test(form.email.trim())) e.email = '有効なメールアドレスを入力してください';
    if (!form.inquiryType) e.inquiryType = 'お問い合わせ種別を選択してください';
    if (form.subject.trim().length < 5) e.subject = '件名は5文字以上で入力してください';
    if (form.content.trim().length < 50) e.content = 'お問い合わせ内容は50文字以上で入力してください';
    return e;
  };

  const handleSubmit = async () => {
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length > 0) return;
    setSubmitting(true);
    const { error } = await supabase.from('contact_inquiries').insert({
      name: form.name.trim(),
      email: form.email.trim(),
      inquiry_type: form.inquiryType,
      subject: form.subject.trim(),
      content: form.content.trim(),
    });
    setSubmitting(false);
    if (error) {
      alert('送信に失敗しました。時間をおいて再度お試しください。');
    } else {
      setSubmitted(true);
    }
  };

  const inputBase = (field) => ({
    width: '100%',
    padding: '11px 14px',
    border: `1.5px solid ${errors[field] ? '#fca5a5' : '#e5e7eb'}`,
    borderRadius: 10,
    fontSize: 14,
    color: '#1a1a2e',
    background: '#fafafa',
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
  });

  const labelStyle = {
    display: 'block',
    fontSize: 13,
    fontWeight: 600,
    color: '#374151',
    marginBottom: 6,
  };

  const errStyle = { fontSize: 12, color: '#ef4444', marginTop: 4 };

  if (submitted) {
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
        <div style={{ background: '#fff', borderRadius: 20, padding: '40px 28px', maxWidth: 420, width: '100%', textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: '#1a1a2e', marginBottom: 10 }}>送信が完了しました</h2>
          <p style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.8, marginBottom: 24 }}>
            お問い合わせいただきありがとうございます。<br />
            内容を確認のうえ、必要に応じてご返信いたします。
          </p>
          <button
            onClick={onClose}
            style={{ padding: '11px 36px', background: '#6d28d9', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 700, color: '#fff', cursor: 'pointer' }}
          >
            閉じる
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '20px 16px 40px' }}
      onClick={onClose}
    >
      <div
        style={{ background: '#fff', borderRadius: 20, width: '100%', maxWidth: 520, marginTop: 16 }}
        onClick={e => e.stopPropagation()}
      >
        {/* ヘッダー */}
        <div style={{ padding: '18px 20px 14px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2 style={{ fontSize: 17, fontWeight: 700, color: '#1a1a2e', margin: 0 }}>お問い合わせ</h2>
          <button onClick={onClose} style={{ background: '#f3f4f6', border: 'none', borderRadius: '50%', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            <X size={17} />
          </button>
        </div>

        <div style={{ padding: '20px 20px 28px' }}>
          {/* 冒頭メッセージ */}
          <div style={{ background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 12, padding: '14px 16px', marginBottom: 24, fontSize: 13, color: '#4b5563', lineHeight: 1.75 }}>
            <p style={{ margin: '0 0 4px', fontWeight: 700, color: '#1a1a2e' }}>HowToonは、より良いサービスを目指して日々改善を重ねているWebサービスです。</p>
            <p style={{ margin: '0 0 4px' }}>内容を確認のうえ、必要に応じて対応させていただきます。</p>
            <p style={{ margin: '0 0 4px' }}>恐れ入りますが、具体的な内容のご記載をお願いいたします。</p>
            <p style={{ margin: 0, fontSize: 12, color: '#9ca3af' }}>※内容が不明瞭な場合や、サービスと関係のないお問い合わせには返信できない場合があります。</p>
          </div>

          {/* お名前 */}
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>
              お名前 <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              type="text"
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="山田 太郎（2文字以上）"
              style={inputBase('name')}
            />
            {errors.name && <p style={errStyle}>{errors.name}</p>}
          </div>

          {/* 返信先メールアドレス */}
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>
              返信先メールアドレス <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              type="email"
              value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              placeholder="example@email.com"
              style={inputBase('email')}
            />
            {errors.email && <p style={errStyle}>{errors.email}</p>}
          </div>

          {/* お問い合わせ種別 */}
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>
              お問い合わせ種別 <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <select
              value={form.inquiryType}
              onChange={e => setForm(f => ({ ...f, inquiryType: e.target.value }))}
              style={{ ...inputBase('inquiryType'), appearance: 'none', WebkitAppearance: 'none', cursor: 'pointer', backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%236b7280' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 14px center', paddingRight: 36 }}
            >
              <option value="">選択してください</option>
              {INQUIRY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            {errors.inquiryType && <p style={errStyle}>{errors.inquiryType}</p>}
          </div>

          {/* 件名 */}
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>
              件名 <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              type="text"
              value={form.subject}
              onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
              placeholder="件名を入力（5文字以上）"
              style={inputBase('subject')}
            />
            {errors.subject && <p style={errStyle}>{errors.subject}</p>}
          </div>

          {/* お問い合わせ内容 */}
          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>
              お問い合わせ内容 <span style={{ color: '#ef4444' }}>*</span>
              <span style={{ fontSize: 11, fontWeight: 400, color: form.content.trim().length >= 50 ? '#10b981' : '#9ca3af', marginLeft: 8 }}>
                {form.content.length}文字 / 50文字以上必須
              </span>
            </label>
            <textarea
              value={form.content}
              onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
              placeholder="お問い合わせ内容を具体的にご記載ください（50文字以上）"
              rows={6}
              style={{ ...inputBase('content'), resize: 'vertical', lineHeight: 1.65 }}
            />
            {errors.content && <p style={errStyle}>{errors.content}</p>}
          </div>

          {/* 送信前注意文 */}
          <div style={{ background: '#f9fafb', border: '1px solid #f3f4f6', borderRadius: 10, padding: '12px 14px', marginBottom: 20, fontSize: 12, color: '#6b7280', lineHeight: 1.75 }}>
            ※内容を確認のうえ、必要に応じて返信いたします。<br />
            すべてのお問い合わせに個別回答をお約束するものではありません。
          </div>

          {/* 送信ボタン */}
          <button
            onClick={handleSubmit}
            disabled={submitting}
            style={{
              width: '100%',
              padding: '13px 0',
              background: submitting ? '#e5e7eb' : '#6d28d9',
              border: 'none',
              borderRadius: 12,
              fontSize: 15,
              fontWeight: 700,
              color: submitting ? '#9ca3af' : '#fff',
              cursor: submitting ? 'not-allowed' : 'pointer',
              transition: 'background 0.15s',
            }}
          >
            {submitting ? '送信中...' : '送信する'}
          </button>
        </div>
      </div>
    </div>
  );
}
